// src/utils/seasonPriceEngine.js

import { db } from "../lib/firebase";
import {
  collection,
  doc,
  getDocs,
  getDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { logError } from "./logError";

/**
 * Season Price Engine (for season rollover pricing)
 *
 * Key rules:
 * - 5 bands only: -1.0, -0.5, 0.0, +0.5, +1.0 (applied to current season originalPrice)
 * - Relative scoring: percentile(rank) based on season price drift + points-per-game
 * - Starting price snapped to 0.5 increments
 * - Clamp final price to [4.0, 8.5]
 * - Optional inflation control: keep league-wide average roughly stable vs last season
 */

/** ---------- Config (tweak later) ---------- */
const PRICE_MIN = 4.0;
const PRICE_MAX = 8.5;
const SNAP = 0.5;

// weighting between relative price drift vs PPG
const WEIGHT_PRICE = 0.65;
const WEIGHT_PPG = 0.35;

// percentile → band thresholds (relative buckets)
const BAND_THRESHOLDS = [
  { maxPct: 10, band: -1.0 },
  { maxPct: 25, band: -0.5 },
  { maxPct: 70, band: 0.0 },
  { maxPct: 85, band: +0.5 },
  { maxPct: 100, band: +1.0 },
];

// inflation control: how close we try to keep average price
const AVG_TOLERANCE = 0.10; // allow avg to drift by 0.1 before adjusting
const ADJUST_STEP = 0.5; // shift by 0.5 steps when controlling inflation

const AVG_PRICE_CAP = 6.0;
const GLOBAL_DEFLATION_STEP = 0.5;

/** ---------- Helpers ---------- */
function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function snapToHalf(n) {
  // snap to nearest 0.5
  return Math.round(n / SNAP) * SNAP;
}

function toGWKey(gw) {
  if (!gw) return null;
  if (typeof gw === "number") return `GW${gw}`;
  return gw.startsWith("GW") ? gw : `GW${gw}`;
}

function percentileRanks(valuesById) {
  // valuesById: { id: number }
  // returns: { id: percentile 0..100 }
  const entries = Object.entries(valuesById).filter(
    ([, v]) => typeof v === "number" && !Number.isNaN(v)
  );

  if (entries.length === 0) return {};

  // Sort ascending by value
  const sorted = [...entries].sort((a, b) => a[1] - b[1]);
  const n = sorted.length;

  // Group IDs by identical value
  const valueGroups = new Map();
  for (const [id, value] of sorted) {
    if (!valueGroups.has(value)) valueGroups.set(value, []);
    valueGroups.get(value).push(id);
  }

  const out = {};
  let rankStart = 0;

  for (const [, ids] of valueGroups.entries()) {
    const groupSize = ids.length;

    // Mid-rank percentile for ties
    const midRank = rankStart + (groupSize - 1) / 2;
    const pct = n === 1 ? 50 : (midRank / (n - 1)) * 100;

    for (const id of ids) {
      out[id] = pct;
    }

    rankStart += groupSize;
  }

  return out;
}

function bandFromPercentile(pct) {
  const p = clamp(typeof pct === "number" ? pct : 50, 0, 100);
  for (const t of BAND_THRESHOLDS) {
    if (p <= t.maxPct) return t.band;
  }
  return 0.0;
}

/**
 * Reads all players for a season.
 * Assumes docs shaped like:
 * - originalPrice (number)
 * - currentPrice (number)
 * - priceHistory (map GWx -> number) [optional]
 */
async function fetchSeasonPlayers(leagueID, seasonID) {
  const playersCol = collection(db, "leagues", leagueID, "seasons", seasonID, "players");
  const snap = await getDocs(playersCol);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Compute new starting prices for the next season.
 *
 * @returns {Promise<{
 *   pricesByPlayer: Record<string, { baseOriginal: number, band: number, newOriginal: number, debug: any }>,
 *   meta: any
 * }>}
 */
export async function computeSeasonStartPrices({
  leagueID,
  priceMin = PRICE_MIN,
  priceMax = PRICE_MAX,
  weightPrice = WEIGHT_PRICE,
  weightPPG = WEIGHT_PPG,
  inflationControl = true,
}) {
  // Debug logging
  const DEBUG = true;
  // Resolve currentSeasonID from league doc
  const leagueRef = doc(db, "leagues", leagueID);
  const leagueSnap = await getDoc(leagueRef);

  if (!leagueSnap.exists()) {
    throw new Error(`League ${leagueID} not found`);
  }

  const leagueData = leagueSnap.data();
  const resolvedSeasonID = leagueData.currentSeasonID;

  if (!resolvedSeasonID) {
    throw new Error("currentSeasonID missing on league document");
  }

  DEBUG && console.log("[SeasonPriceEngine] Resolved currentSeasonID from league", resolvedSeasonID);
  DEBUG && console.log("[SeasonPriceEngine] Starting computation", {
    leagueID,
    resolvedSeasonID,
    weightPrice,
    weightPPG,
    priceMin,
    priceMax,
    inflationControl,
  });
  DEBUG && console.log("[SeasonPriceEngine] IDs", { leagueID, resolvedSeasonID });
  try {
    const players = await fetchSeasonPlayers(leagueID, resolvedSeasonID);
    DEBUG && console.log(
      "[SeasonPriceEngine] Loaded players",
      players.map(p => ({
        id: p.id,
        originalPrice: p.originalPrice,
        currentPrice: p.currentPrice,
      }))
    );

    // Compute season drift + PPG per player directly from player doc
    // Always key all maps by Firestore doc.id (never by any p.id data field)
    const driftByPlayer = {};
    const ppgByPlayer = {};
    const baseOriginalByPlayer = {};

    for (const playerDoc of players) {
      // Always use Firestore doc.id as the key
      const playerID = playerDoc.id;

      // Defensive normalization: originalPrice is always the base reference; fallback to currentPrice only if originalPrice missing
      let baseOriginal = 0;
      if (typeof playerDoc.originalPrice === "number") {
        baseOriginal = playerDoc.originalPrice;
      } else if (typeof playerDoc.currentPrice === "number") {
        baseOriginal = playerDoc.currentPrice;
      }
      baseOriginalByPlayer[playerID] = baseOriginal;

      // season start price must always originate from originalPrice (fallback only if missing)
      const startPrice = baseOriginal;

      // end price = currentPrice if present else last priceHistory entry
      let endPrice = typeof playerDoc.currentPrice === "number" ? playerDoc.currentPrice : null;
      if (endPrice == null && playerDoc.priceHistory && typeof playerDoc.priceHistory === "object") {
        const keys = Object.keys(playerDoc.priceHistory)
          .filter(k => k.startsWith("GW"))
          .sort((a, b) => {
            const na = parseInt(a.replace("GW", ""), 10);
            const nb = parseInt(b.replace("GW", ""), 10);
            return na - nb;
          });
        if (keys.length > 0) {
          endPrice = playerDoc.priceHistory[keys[keys.length - 1]];
        }
      }
      if (endPrice == null) endPrice = baseOriginal;

      const drift = Number((endPrice - startPrice).toFixed(4));
      driftByPlayer[playerID] = drift;

      // points + appearances from pointsHistory
      const pointsHistory = playerDoc.pointsHistory || {};
      const entries = Object.entries(pointsHistory);
      const totalPoints = entries.reduce((sum, [, pts]) => {
        return sum + (typeof pts === "number" ? pts : 0);
      }, 0);
      const appearances = entries.length;
      const ppg = appearances > 0 ? totalPoints / appearances : 0;
      ppgByPlayer[playerID] = Number(ppg.toFixed(4));
    }
    DEBUG && console.log(
      "[SeasonPriceEngine] Raw metrics per player",
      Object.keys(baseOriginalByPlayer).map(pid => ({
        playerID: pid,
        baseOriginal: baseOriginalByPlayer[pid],
        drift: driftByPlayer[pid],
        ppg: ppgByPlayer[pid],
      }))
    );

    // Convert to percentiles (relative)
    const driftPct = percentileRanks(driftByPlayer);
    const ppgPct = percentileRanks(ppgByPlayer);

    // Combined score percentile-ish (0..100) using weighted percentiles
    const combinedPctByPlayer = {};
    for (const pid of Object.keys(baseOriginalByPlayer)) {
      const dp = typeof driftPct[pid] === "number" ? driftPct[pid] : 50;
      const pp = typeof ppgPct[pid] === "number" ? ppgPct[pid] : 50;
      const combined = dp * weightPrice + pp * weightPPG;
      combinedPctByPlayer[pid] = Number(combined.toFixed(4));
    }
    DEBUG && console.log(
      "[SeasonPriceEngine] Percentiles",
      Object.keys(combinedPctByPlayer).map(pid => ({
        playerID: pid,
        driftPct: driftPct[pid],
        ppgPct: ppgPct[pid],
        combinedPct: combinedPctByPlayer[pid],
      }))
    );

    // Assign band + compute new original
    // Always key by Firestore doc.id
    const pricesByPlayer = {};
    for (const playerID of Object.keys(baseOriginalByPlayer)) {
      const baseOriginal = baseOriginalByPlayer[playerID] || 0;
      const pct = combinedPctByPlayer[playerID] ?? 50;
      const band = bandFromPercentile(pct);

      let newOriginal = baseOriginal + band;
      newOriginal = snapToHalf(newOriginal);
      newOriginal = clamp(newOriginal, priceMin, priceMax);

      pricesByPlayer[playerID] = {
        baseOriginal,
        band,
        newOriginal,
        debug: {
          drift: driftByPlayer[playerID] ?? null,
          ppg: ppgByPlayer[playerID] ?? null,
          driftPct: driftPct[playerID] ?? null,
          ppgPct: ppgPct[playerID] ?? null,
          combinedPct: pct,
        },
      };
    }
    DEBUG && console.log(
      "[SeasonPriceEngine] Banded prices (pre-deflation)",
      Object.entries(pricesByPlayer).map(([pid, v]) => ({
        playerID: pid,
        baseOriginal: v.baseOriginal,
        band: v.band,
        newOriginal: v.newOriginal,
      }))
    );
    const avgBefore =
      Object.values(pricesByPlayer).reduce((s, p) => s + p.newOriginal, 0) /
      Math.max(Object.keys(pricesByPlayer).length, 1);

    DEBUG && console.log(
      "[SeasonPriceEngine] League average before deflation",
      avgBefore.toFixed(2)
    );

    // Optional: global deflation if average price exceeds cap
    let globalDeflationApplied = false;
    if (inflationControl) {
      const ids = Object.keys(pricesByPlayer);
      const avgNew =
        ids.reduce((s, pid) => s + (pricesByPlayer[pid].newOriginal || 0), 0) /
        Math.max(ids.length, 1);

      if (avgNew > AVG_PRICE_CAP) {
        DEBUG && console.warn(
          "[SeasonPriceEngine] Global deflation applied (-0.5 to all players)"
        );
        globalDeflationApplied = true;
        for (const pid of ids) {
          const cur = pricesByPlayer[pid].newOriginal;
          if (typeof cur !== "number") continue;

          let next = cur - GLOBAL_DEFLATION_STEP;
          next = snapToHalf(next);
          next = clamp(next, priceMin, priceMax);

          pricesByPlayer[pid].newOriginal = next;
        }
        const avgAfter =
          Object.values(pricesByPlayer).reduce((s, p) => s + p.newOriginal, 0) /
          Math.max(Object.keys(pricesByPlayer).length, 1);
        DEBUG && console.log(
          "[SeasonPriceEngine] League average after deflation",
          avgAfter.toFixed(2)
        );
      }
    }

    DEBUG && console.log(
      "[SeasonPriceEngine] Final output",
      {
        players: Object.entries(pricesByPlayer).map(([pid, v]) => ({
          playerID: pid,
          finalPrice: v.newOriginal,
        })),
      }
    );
    // Final validation: ensure all player keys have a numeric newOriginal
    for (const [playerID, info] of Object.entries(pricesByPlayer)) {
      if (typeof info?.newOriginal !== "number" || Number.isNaN(info.newOriginal)) {
        throw new Error(
          `[SeasonPriceEngine] Invalid newOriginal for playerID=${playerID}: ${JSON.stringify(info)}`
        );
      }
    }

    return {
      pricesByPlayer,
      meta: {
        weights: { price: weightPrice, ppg: weightPPG },
        clamp: { min: priceMin, max: priceMax },
        snap: SNAP,
        inflationControl,
        globalDeflationApplied,
      },
    };
  } catch (err) {
    await logError({
      leagueID,
      userID: "system",
      action: "computeSeasonStartPrices",
      payload: { currentSeasonID: resolvedSeasonID },
      error: err,
    });
    throw err;
  }
}

/**
 * Apply computed season start prices into the *next* season player docs.
 * This updates:
 * - originalPrice
 * - currentPrice
 * - priceHistory.GW1
 * - priceAccumulator (reset)
 * - priceDelta (reset)
 *
 * (It assumes the player docs already exist in nextSeasonID, i.e. you copied players over in advanceSeason)
 */
export async function applySeasonStartPricesToNextSeason({
  leagueID,
  nextSeasonID,
  pricesByPlayer,
}) {
  try {
    const batch = writeBatch(db);

    for (const [playerID, info] of Object.entries(pricesByPlayer || {})) {
      const newPrice = info?.newOriginal;
      if (typeof newPrice !== "number") continue;

      const playerRef = doc(db, "leagues", leagueID, "seasons", nextSeasonID, "players", playerID);

      batch.update(playerRef, {
        originalPrice: newPrice,
        currentPrice: newPrice,
        priceAccumulator: 0,
        priceDelta: 0,
        "priceHistory.GW1": newPrice,
      });
    }

    await batch.commit();
    return true;
  } catch (err) {
    await logError({
      leagueID,
      userID: "system",
      action: "applySeasonStartPricesToNextSeason",
      payload: { nextSeasonID },
      error: err,
    });
    throw err;
  }
}

export const seasonPriceEngine = computeSeasonStartPrices;